import React, { useState, useEffect, useRef } from "react";
import { DatePicker, Space } from "antd";
import dayjs from "dayjs";

const FORMAT = "YYYY-MM-DD";

const CustomDateRange = ({ startDate, endDate, onRangeChange }) => {
  const [draft, setDraft] = useState({ start: startDate, end: endDate });
  const [activePicker, setActivePicker] = useState(null);
  const startRef = useRef(null);
  const endRef = useRef(null);
  const pickedRef = useRef(new Set());
  const awaitingRef = useRef(null);
  const awaitingOpenedRef = useRef(false);

  useEffect(() => {
    setDraft({ start: startDate, end: endDate });
    pickedRef.current = new Set();
    awaitingRef.current = null;
  }, [startDate, endDate]);

  useEffect(() => {
    if (activePicker === "start") startRef.current?.focus();
    if (activePicker === "end") endRef.current?.focus();
    if (activePicker && activePicker === awaitingRef.current) {
      awaitingOpenedRef.current = true;
    }
  }, [activePicker]);

  const commit = (range) => {
    pickedRef.current = new Set();
    awaitingRef.current = null;
    if (range.start !== startDate || range.end !== endDate) {
      onRangeChange(range.start, range.end);
    }
  };

  const handleOpenChange = (picker) => (open) => {
    if (
      !open &&
      awaitingRef.current === picker &&
      awaitingOpenedRef.current &&
      draft.start &&
      draft.end
    ) {
      commit(draft);
    }
    setActivePicker((current) =>
      open ? picker : current === picker ? null : current,
    );
  };

  const pick = (picker, value) => {
    if (!value) return;
    const next = { ...draft, [picker]: value };
    const picked = pickedRef.current;
    picked.add(picker);
    // New start is past the end: force the user to choose a new end
    if (next.start && next.end && next.start > next.end) {
      next.end = null;
      picked.delete("end");
    }
    setDraft(next);

    const complete = picked.has("start") && picked.has("end");
    if (complete && next.start && next.end) {
      setActivePicker(null);
      commit(next);
      return;
    }
    const other = picker === "start" ? "end" : "start";
    awaitingRef.current = other;
    awaitingOpenedRef.current = false;
    setActivePicker(other);
  };

  const popupContainer = (trigger) =>
    trigger?.closest?.(".analytics-date-filter-wrap") || document.body;

  return (
    <Space size={6}>
      <DatePicker
        ref={startRef}
        open={activePicker === "start"}
        onOpenChange={handleOpenChange("start")}
        value={draft.start ? dayjs(draft.start) : null}
        onChange={(_, dateString) => pick("start", dateString)}
        style={{ width: 140 }}
        className="date-input date-picker-input"
        getPopupContainer={popupContainer}
        placement="bottomLeft"
        format={FORMAT}
        allowClear={false}
      />
      <DatePicker
        ref={endRef}
        open={activePicker === "end"}
        onOpenChange={handleOpenChange("end")}
        value={draft.end ? dayjs(draft.end) : null}
        onChange={(_, dateString) => pick("end", dateString)}
        disabledDate={(current) =>
          draft.start && current && current < dayjs(draft.start).startOf("day")
        }
        style={{ width: 140 }}
        className="date-input date-picker-input"
        getPopupContainer={popupContainer}
        placement="bottomLeft"
        format={FORMAT}
        allowClear={false}
      />
    </Space>
  );
};

export default CustomDateRange;
